import { useGet } from 'common/hooks/useApi'
import { IPaginatedResponse } from 'common/types/api.types'
import { useAtom } from 'jotai'
import { debounce } from 'lodash'
import { useEffect } from 'react'
import { useDebounce } from 'react-use'
import { ApiPathsJobs } from 'services/jobs/jobs.service'
import { IJob } from 'services/jobs/jobs.types'
import styled from 'styled-components'
import { CommunityJobs, IJobsListQuery } from './CommunityJobs'
import { initialQueryStateAtom, queryInitialState } from './CommunityJobs.state'
import { CommunityJobsFilters } from './CommunityJobsFilters'
import { CommunityJobsMobileFilters } from './CommunityJobsMobileFilters'

export const CommunityJobsSection = () => {
  const [query, setQuery] = useAtom(initialQueryStateAtom)
  const { isLoading, data } = useGet<IPaginatedResponse<IJob[]>>(
    ApiPathsJobs.getPublicJobs(query.queries[query.queries.length - 1]),
  )

  const generateGetJobsQuery = (query: IJobsListQuery) => {
    const { page, search, sortBy, filters } = query
    let searchQuery = `?page=${page}&sortBy=${sortBy}`

    if (search) searchQuery += `&search=${search}`

    const positionsFilters = filters.positions.join(',')
    if (positionsFilters) searchQuery += `&filter.position.id=$in:${positionsFilters}`

    const jobCategoriesFilters = filters.jobCategories.join(',')
    if (jobCategoriesFilters) searchQuery += `&filter.jobCategory.id=$in:${jobCategoriesFilters}`

    return searchQuery
  }

  useDebounce(
    // Fixes the bug where you visit a job and when you press back, this debounce was resetting the queries
    () => !!data && query.search !== null && setQuery((c) => ({ ...c, queries: [generateGetJobsQuery(query)] })),
    700,
    [query.search],
  )

  useEffect(() => {
    if (JSON.stringify(query.filters) !== JSON.stringify(queryInitialState.filters)) {
      setQuery((c) => ({ ...c, queries: [generateGetJobsQuery(query)] }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.filters])

  useEffect(() => {
    if (query.scrollPosition !== 0) {
      window.scrollTo({ top: query.scrollPosition, left: 0, behavior: 'auto' })
    }

    const handleScroll = debounce(() => {
      setQuery((c) => ({ ...c, scrollPosition: window.scrollY }))
    }, 500)

    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('scroll', handleScroll)
      handleScroll.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Div className="flex flex-column md:py-5 md:flex-row">
      <div className="md:hidden p-3">
        <CommunityJobsMobileFilters isLoading={isLoading} query={query} setQuery={setQuery} />
      </div>

      <aside className="hidden md:block p-3">
        <CommunityJobsFilters query={query} setQuery={setQuery} />
      </aside>

      <main className="flex-1">
        <CommunityJobs query={query} setQuery={setQuery} isLoading={isLoading} data={data} />
      </main>
    </Div>
  )
}

export const Div = styled.div`
  aside {
    flex-basis: 22%;
  }
`
